import { IconType } from "react-icons";
import { FaFileAlt, FaPhoneAlt, FaIdCard, FaImage, FaFileInvoiceDollar, FaChartLine, FaBuilding, FaUser, FaCar, FaCamera, FaFileContract, FaBookOpen, FaBalanceScale, FaComments } from "react-icons/fa";

export interface AMPConfig {
  id: string;
  title: string;
  description: string;
  icon: IconType;
  color: string;
  placeholder?: string;
  emptyStateTitle?: string;
  emptyStateSubtitle?: string;
  instructions?: string;
  acceptedFileTypes?: string;
}

export const AMP_CONFIGS: Record<string, AMPConfig> = {
  "support-assistant": {
    id: "support-assistant",
    title: "Support Assistant",
    description: "Get instant answers for support-related queries.",
    icon: FaComments,
    color: "#6b46c1",
    placeholder: "Ask me anything about support...",
    emptyStateTitle: "My AMP Insurance Assistance",
    emptyStateSubtitle: "Get instant answers for support-related queries and assistance",
    instructions: "I'm your Support Assistant. Ask me anything and I'll help you find the answers!"
  },
  "ai-scans": {
    id: "ai-scans",
    title: "AI-Powered Scans",
    description: "Extract structured data from your documents.",
    icon: FaFileAlt,
    color: "#6b46c1",
    placeholder: "Ask about document scanning or upload a file...",
    emptyStateTitle: "AI-Powered Document Scanning",
    emptyStateSubtitle: "Upload your documents and I'll extract structured data automatically",
    instructions: "I'm your AI-Powered Scans assistant. Upload a document and I'll extract structured data for you!",
    acceptedFileTypes: ".pdf,.doc,.docx,.jpg,.jpeg,.png"
  },
  "call-assistant": {
    id: "call-assistant",
    title: "Call Assistant",
    description: "Summarize customer calls using AI insights.",
    icon: FaPhoneAlt,
    color: "rgba(107, 70, 193, 0.8)",
    placeholder: "Ask about call summaries or analysis...",
    emptyStateTitle: "AI Call Assistant",
    emptyStateSubtitle: "Upload call recordings and I'll provide detailed summaries and insights",
    instructions: "I'm your Call Assistant. I can help you summarize and analyze customer calls.",
    acceptedFileTypes: ".mp3,.wav,.m4a,.mp4"
  },
  "coi-assistant": {
    id: "coi-assistant",
    title: "COI Assistant",
    description: "Generate COIs easily with smart form filling.",
    icon: FaFileContract,
    color: "#6b46c1",
    placeholder: "Ask about COI generation...",
    emptyStateTitle: "COI Assistant",
    emptyStateSubtitle: "Generate Certificates of Insurance with AI-powered smart form filling",
    instructions: "I'm your COI Assistant. I can help you generate Certificates of Insurance quickly."
  },
  "image-generator": {
    id: "image-generator",
    title: "Image Generator",
    description: "Create images from text prompts using AI.",
    icon: FaImage,
    color: "#6b46c1",
    placeholder: "Describe the image you want to create...",
    emptyStateTitle: "AI Image Generator",
    emptyStateSubtitle: "Create stunning images from text descriptions using AI",
    instructions: "I'm your Image Generator. Describe what you want to create and I'll generate it for you!"
  },
  "driver-license-scanner": {
    id: "driver-license-scanner",
    title: "Driver License Scanner",
    description: "Scan and extract information from driver licenses using AI.",
    icon: FaIdCard,
    color: "#6b46c1",
    placeholder: "Upload a driver license to scan...",
    emptyStateTitle: "Driver License Scanner",
    emptyStateSubtitle: "Upload driver licenses and extract information automatically",
    instructions: "I'm your Driver License Scanner. Upload a driver license and I'll extract all the information.",
    acceptedFileTypes: ".jpg,.jpeg,.png,.pdf"
  },
  "commission-scanner": {
    id: "commission-scanner",
    title: "Commission Scanner",
    description: "Scan and extract information from commission documents using AI.",
    icon: FaFileInvoiceDollar,
    color: "#6b46c1",
    placeholder: "Upload commission documents to scan...",
    emptyStateTitle: "Commission Scanner",
    emptyStateSubtitle: "Scan and extract commission data from your documents",
    instructions: "I'm your Commission Scanner. Upload commission documents and I'll extract all relevant data.",
    acceptedFileTypes: ".pdf,.xlsx,.xls,.csv"
  },
  "loss-run-scanner": {
    id: "loss-run-scanner",
    title: "Loss Run Scanner",
    description: "Scan and extract information from loss run documents using AI.",
    icon: FaFileAlt,
    color: "#6b46c1",
    placeholder: "Upload loss run documents to scan...",
    emptyStateTitle: "Loss Run Scanner",
    emptyStateSubtitle: "Extract structured data from loss run documents",
    instructions: "I'm your Loss Run Scanner. Upload loss run documents and I'll extract all the key information.",
    acceptedFileTypes: ".pdf,.doc,.docx"
  },
  "risk-appetite": {
    id: "risk-appetite",
    title: "Risk Appetite",
    description: "Get risk appetite insights using AI.",
    icon: FaChartLine,
    color: "#6b46c1",
    placeholder: "Ask about risk appetite analysis...",
    emptyStateTitle: "Risk Appetite Analysis",
    emptyStateSubtitle: "Get AI-powered insights on risk appetite and underwriting decisions",
    instructions: "I'm your Risk Appetite Assistant. I can help you analyze and understand risk appetite for various scenarios."
  },
  "data-enrichment-wc": {
    id: "data-enrichment-wc",
    title: "Data Enrichment WC",
    description: "Enrich data for your commercial prospects using AI.",
    icon: FaBuilding,
    color: "#6b46c1",
    placeholder: "Enter company information to enrich...",
    emptyStateTitle: "Workers' Comp Data Enrichment",
    emptyStateSubtitle: "Enrich your commercial prospect data with AI-powered insights",
    instructions: "I'm your Workers' Comp Data Enrichment Assistant. Provide company details and I'll enrich them with relevant data."
  },
  "data-enrichment-pl": {
    id: "data-enrichment-pl",
    title: "Data Enrichment PL",
    description: "Enrich data for your personal lines prospects using AI.",
    icon: FaUser,
    color: "#6b46c1",
    placeholder: "Enter customer information to enrich...",
    emptyStateTitle: "Personal Lines Data Enrichment",
    emptyStateSubtitle: "Enrich your personal lines prospect data with AI-powered insights",
    instructions: "I'm your Personal Lines Data Enrichment Assistant. Provide customer details and I'll enrich them with relevant data."
  },
  "get-mvrs": {
    id: "get-mvrs",
    title: "Get MVRs",
    description: "Get Motor Vehicle Records using AI.",
    icon: FaCar,
    color: "#6b46c1",
    placeholder: "Enter driver information to retrieve MVR...",
    emptyStateTitle: "Get Motor Vehicle Records",
    emptyStateSubtitle: "Retrieve Motor Vehicle Records using AI-powered search",
    instructions: "I'm your MVR Retrieval Assistant. Provide driver information and I'll help you get their MVR."
  },
  "scan-mvrs": {
    id: "scan-mvrs",
    title: "Scan MVRs",
    description: "Scan Motor Vehicle Records using AI.",
    icon: FaCamera,
    color: "#6b46c1",
    placeholder: "Upload MVR documents to scan...",
    emptyStateTitle: "Scan Motor Vehicle Records",
    emptyStateSubtitle: "Extract structured data from MVR documents",
    instructions: "I'm your MVR Scanner. Upload MVR documents and I'll extract all relevant information.",
    acceptedFileTypes: ".pdf,.jpg,.jpeg,.png"
  },
  "policy-summarizer": {
    id: "policy-summarizer",
    title: "Policy Summarizer",
    description: "Summarize insurance policies using AI.",
    icon: FaBookOpen,
    color: "#6b46c1",
    placeholder: "Upload a policy to summarize...",
    emptyStateTitle: "Policy Summarizer",
    emptyStateSubtitle: "Get AI-powered summaries of insurance policy documents",
    instructions: "I'm your Policy Summarizer. Upload a policy document and I'll provide a comprehensive summary.",
    acceptedFileTypes: ".pdf,.doc,.docx"
  },
  "policy-comparator": {
    id: "policy-comparator",
    title: "Policy Comparison",
    description: "Compare insurance policies using AI.",
    icon: FaBalanceScale,
    color: "#6b46c1",
    placeholder: "Upload policies to compare...",
    emptyStateTitle: "Policy Comparison",
    emptyStateSubtitle: "Compare multiple insurance policies side-by-side with AI analysis",
    instructions: "I'm your Policy Comparator. Upload multiple policies and I'll provide a detailed comparison.",
    acceptedFileTypes: ".pdf,.doc,.docx"
  },
  "quote-proposal-generator": {
    id: "quote-proposal-generator",
    title: "Quote Proposal Generator",
    description: "Generate quote proposals using AI.",
    icon: FaFileInvoiceDollar,
    color: "#6b46c1",
    placeholder: "Provide details to generate a quote proposal...",
    emptyStateTitle: "Quote Proposal Generator",
    emptyStateSubtitle: "Generate professional quote proposals with AI assistance",
    instructions: "I'm your Quote Proposal Generator. Provide the details and I'll generate a professional proposal for you."
  }
};

export const getAMPConfig = async (ampId: string): Promise<AMPConfig | null> => {
  try {
    if (AMP_CONFIGS[ampId]) {
      return AMP_CONFIGS[ampId];
    }
    const response = await fetch(`/api/amps/${ampId}`);
    if (response.ok) {
      const apiData = await response.json();
      return {
        id: apiData.id || ampId,
        title: apiData.name || apiData.title,
        description: apiData.description,
        icon: FaFileAlt,
        color: apiData.color || "#6b46c1",
        placeholder: apiData.placeholder,
        emptyStateTitle: apiData.emptyStateTitle,
        emptyStateSubtitle: apiData.emptyStateSubtitle,
        instructions: apiData.instructions,
        acceptedFileTypes: apiData.acceptedFileTypes
      };
    }
  } catch (error) {
    console.error('Error fetching AMP config:', error);
  }
  
  return null;
};

export const getStaticAMPConfig = (ampId: string): AMPConfig | undefined => {
  return AMP_CONFIGS[ampId];
};

export const getAllAMPConfigs = (): AMPConfig[] => {
  return Object.values(AMP_CONFIGS);
};

export const getAllAMPs = async (): Promise<AMPConfig[]> => {
  try {
    const response = await fetch('/api/amps');
    if (response.ok) {
      const apiAmps = await response.json();
      const userAmps = apiAmps.map((amp: any) => ({
        id: amp.id,
        title: amp.name || amp.title,
        description: amp.description,
        icon: FaFileAlt,
        color: amp.color || "#6b46c1",
        placeholder: amp.placeholder,
        emptyStateTitle: amp.emptyStateTitle,
        emptyStateSubtitle: amp.emptyStateSubtitle,
        instructions: amp.instructions,
        acceptedFileTypes: amp.acceptedFileTypes
      }));
      
      return [...Object.values(AMP_CONFIGS), ...userAmps];
    }
  } catch (error) {
    console.error('Error fetching all AMPs:', error);
  }
  
  return Object.values(AMP_CONFIGS);
};
